import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  AssignmentInd as AssignIcon,
  AttachFile as AttachIcon,
  CheckCircle as IdentifiedIcon,
  Close as CloseIcon,
  Done as SentIcon,
  DoneAll as DeliveredIcon,
  ErrorOutline as FailedIcon,
  MoreVert as MoreIcon,
  PersonSearch as PersonSearchIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { Permission, UserRole } from '../../types/auth';
import type {
  WhatsAppAgentSummary,
  WhatsAppApprovedTemplate,
  WhatsAppCompanyGroup,
  WhatsAppConversation,
  WhatsAppDirectoryContact,
  WhatsAppIntakeOption,
  WhatsAppIntakePrefill,
  WhatsAppMessage,
} from '../../types/whatsapp';
import {
  WhatsAppConversationStatus,
  WhatsAppMessageDirection,
  WHATSAPP_NO_COMPANY_ID,
  WHATSAPP_NO_COMPANY_LABEL,
} from '../../types/whatsapp';
import { useWhatsAppService } from '../../services/whatsappService';
import { useAccountService } from '../../services/accountService';
import { BRAND_COLORS } from '../../constants/branding';
import CaptureWizardModal from '../../components/modals/CaptureWizardModal';
import NewOutboundConversationDialog from '../../components/whatsapp/NewOutboundConversationDialog';
import AssociateIntakeDialog from '../../components/whatsapp/AssociateIntakeDialog';

const POLL_INTERVAL_MS = 10_000;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const statusLabel: Record<WhatsAppConversationStatus, string> = {
  NEW: 'Nueva',
  IN_PROGRESS: 'En atención',
  WAITING: 'En espera',
  CLOSED: 'Cerrada',
};

const statusColor: Record<
  WhatsAppConversationStatus,
  'default' | 'primary' | 'success' | 'warning' | 'secondary'
> = {
  NEW: 'primary',
  IN_PROGRESS: 'success',
  WAITING: 'warning',
  CLOSED: 'default',
};

const formatClock = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  const sameDay =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Ayer';
  }
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

const contactTitle = (c: WhatsAppConversation): string =>
  c.contact.displayName?.trim() || c.contact.phone;

/** Tras el primer mensaje saliente, el agente espera respuesta del contacto. */
const isAwaitingContactReply = (c: WhatsAppConversation): boolean => Boolean(c.awaitingContactReply);

const errorMessage = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback;

const PENDING_INTAKE_DRAFTS_KEY = 'sipae.whatsapp.pendingIntakeDrafts';

const parseIntakeId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const readPendingIntakeDrafts = (): Record<string, number> => {
  try {
    const raw = sessionStorage.getItem(PENDING_INTAKE_DRAFTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};
    Object.entries(parsed).forEach(([key, value]) => {
      const id = parseIntakeId(value);
      if (id) out[key] = id;
    });
    return out;
  } catch {
    return {};
  }
};

/** Cola de trabajo del agente. Las dos primeras son la vista principal. */
type AgentQueue = 'available' | 'mine' | 'mineClosed' | 'others';

const isClosedConversation = (c: WhatsAppConversation) =>
  c.status === WhatsAppConversationStatus.CLOSED;

const matchesAgentQueue = (c: WhatsAppConversation, queue: AgentQueue, currentUserId: string | null) => {
  const mine = currentUserId != null && c.assignedAgentId === currentUserId;
  const open = !isClosedConversation(c);
  if (queue === 'available') {
    return !c.assignedAgentId && c.status === WhatsAppConversationStatus.NEW;
  }
  if (queue === 'mine') return mine && open;
  if (queue === 'mineClosed') return mine && isClosedConversation(c);
  return Boolean(c.assignedAgentId) && !mine && open;
};

const queueRowHint = (c: WhatsAppConversation, queue: AgentQueue): string | null => {
  if (queue === 'mine') return c.awaitingContactReply ? 'Esperando respuesta' : 'Falta responder';
  if (queue === 'others') return c.assignedAgentName ? `Agente: ${c.assignedAgentName}` : 'Otro agente';
  if (queue === 'mineClosed') return 'Cerrada';
  return null;
};

const queueEmptyMessage: Record<AgentQueue, string> = {
  available: 'No hay conversaciones por tomar.',
  mine: 'No tienes conversaciones asignadas.',
  mineClosed: 'No tienes conversaciones cerradas.',
  others: 'No hay conversaciones abiertas de otros agentes.',
};

/** Look & feel inspirado en WhatsApp Web (solo hilo de mensajes). */
const CHAT_WALLPAPER_SX = {
  bgcolor: '#e5ddd5',
  backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180'>
      <g fill='none' stroke='%23cbbfb3' stroke-width='1' opacity='0.45'>
        <path d='M20 30c8-12 22-10 28 2M55 70c-6 10-2 22 10 24M110 40c10-4 18 6 14 16M140 100c-8 8-4 20 8 22M40 130c12-2 18 10 10 18M90 150c6-10 20-8 22 4'/>
        <circle cx='70' cy='40' r='3'/><circle cx='150' cy='60' r='2.5'/><circle cx='30' cy='100' r='2'/><circle cx='120' cy='130' r='3'/>
        <path d='M100 90l8 4-4 8-8-4z'/><path d='M50 160c4-6 12-4 12 2'/>
      </g>
    </svg>`
  )}")`,
  backgroundRepeat: 'repeat',
  backgroundSize: '180px 180px',
} as const;

const DeliveryMark: React.FC<{ message: WhatsAppMessage }> = ({ message }) => {
  const iconSx = { fontSize: '0.85rem', verticalAlign: 'middle', ml: 0.25 };
  switch (message.deliveryStatus) {
    case 'SENT':
      return <SentIcon sx={{ ...iconSx, color: '#667781' }} />;
    case 'DELIVERED':
      return <DeliveredIcon sx={{ ...iconSx, color: '#667781' }} />;
    case 'READ':
      return <DeliveredIcon sx={{ ...iconSx, color: '#53bdeb' }} />;
    case 'FAILED':
      return (
        <Tooltip title={message.errorDetail || 'No entregado'}>
          <FailedIcon sx={{ ...iconSx, color: 'error.main' }} />
        </Tooltip>
      );
    default:
      return null;
  }
};

const ChatMessageBubbles: React.FC<{ messages: WhatsAppMessage[] }> = ({ messages }) => (
  <Stack spacing={0.75} sx={{ py: 0.5 }}>
    {messages.map((m) => {
      const mine = m.direction === WhatsAppMessageDirection.OUTBOUND;
      return (
        <Box
          key={m.id}
          sx={{
            display: 'flex',
            justifyContent: mine ? 'flex-end' : 'flex-start',
            px: 0.5,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              maxWidth: '78%',
              px: 1.25,
              pt: 0.75,
              pb: 0.5,
              bgcolor: mine ? '#d9fdd3' : '#ffffff',
              color: '#111b21',
              borderRadius: mine ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
              boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.4,
                fontSize: '0.9rem',
                pr: 4,
              }}
            >
              {m.body}
            </Typography>
            <Typography
              component="span"
              sx={{
                display: 'block',
                textAlign: 'right',
                mt: 0.25,
                fontSize: '0.68rem',
                lineHeight: 1,
                color: '#667781',
                float: 'right',
                ml: 1,
                position: 'relative',
                top: 2,
              }}
            >
              {formatClock(m.sentAt)}
              {mine && <DeliveryMark message={m} />}
            </Typography>
            <Box sx={{ clear: 'both' }} />
          </Box>
        </Box>
      );
    })}
  </Stack>
);

const WhatsAppInboxPage: React.FC = () => {
  const { user, hasPermission, hasAnyRole } = useAuth();
  const canSupervise = hasPermission(Permission.MANAGE_WHATSAPP_ASSIGNMENTS);
  const canOpenIntake = hasAnyRole([UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.PHONE_AGENT]);
  const currentUserId = user?.id != null ? String(user.id) : null;

  const whatsappService = useWhatsAppService();
  const accountService = useAccountService();
  // Los hooks de servicio se recrean en cada render; el ref evita reiniciar el polling.
  const serviceRef = useRef(whatsappService);
  serviceRef.current = whatsappService;

  const [viewMode, setViewMode] = useState<'agent' | 'supervisor'>(
    canSupervise && !hasAnyRole([UserRole.PHONE_AGENT]) ? 'supervisor' : 'agent'
  );
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [detail, setDetail] = useState<WhatsAppConversation | null>(null);
  const [directory, setDirectory] = useState<WhatsAppDirectoryContact[]>([]);
  const [companies, setCompanies] = useState<WhatsAppCompanyGroup[]>([]);
  const [allCompanies, setAllCompanies] = useState<WhatsAppCompanyGroup[]>([]);
  const [templates, setTemplates] = useState<WhatsAppApprovedTemplate[]>([]);
  const [agents, setAgents] = useState<WhatsAppAgentSummary[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const [agentLeftTab, setAgentLeftTab] = useState<'chats' | 'contacts'>('chats');
  const [contactGroupFilter, setContactGroupFilter] = useState<string>('ALL');
  const [contactSearch, setContactSearch] = useState('');
  const [outboundOpen, setOutboundOpen] = useState(false);
  const [associateIntakeOpen, setAssociateIntakeOpen] = useState(false);
  const [pickCompanyOpen, setPickCompanyOpen] = useState(false);
  const [pickCompanyId, setPickCompanyId] = useState('');
  const [intakePrefill, setIntakePrefill] = useState<WhatsAppIntakePrefill | null>(null);
  const [intakeConversationId, setIntakeConversationId] = useState<string | null>(null);
  const [resumePhoneCallId, setResumePhoneCallId] = useState<number | null>(null);
  const [pendingIntakeDrafts, setPendingIntakeDrafts] = useState<Record<string, number>>(readPendingIntakeDrafts);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [editContactForm, setEditContactForm] = useState({ displayName: '', email: '', companyId: '' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [agentQueue, setAgentQueue] = useState<AgentQueue>('available');
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [toastOpen, setToastOpen] = useState(true);
  const [assignAgentId, setAssignAgentId] = useState('');
  const [supervisorDetailTab, setSupervisorDetailTab] = useState<'conversation' | 'assignment'>('conversation');
  const [supStatusFilter, setSupStatusFilter] = useState<string>('ALL');
  const [supAgentFilter, setSupAgentFilter] = useState<string>('ALL');
  const [supSearch, setSupSearch] = useState('');
  const [captureWizardOpen, setCaptureWizardOpen] = useState(false);

  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;
  const intakeConversationIdRef = useRef<string | null>(null);
  intakeConversationIdRef.current = intakeConversationId;

  const rememberIntakeDraft = useCallback((conversationId: string, phoneCallId: number) => {
    setPendingIntakeDrafts((prev) => {
      const next = { ...prev, [conversationId]: phoneCallId };
      sessionStorage.setItem(PENDING_INTAKE_DRAFTS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const forgetIntakeDraft = useCallback((conversationId: string) => {
    setPendingIntakeDrafts((prev) => {
      if (!(conversationId in prev)) return prev;
      const next = { ...prev };
      delete next[conversationId];
      sessionStorage.setItem(PENDING_INTAKE_DRAFTS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const intakeIdFor = useCallback(
    (conv: WhatsAppConversation | null | undefined) => {
      if (!conv) return null;
      return parseIntakeId(conv.intakeId) ?? pendingIntakeDrafts[conv.id] ?? null;
    },
    [pendingIntakeDrafts]
  );

  const notify = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'error') =>
    setSnackbar({ message, severity });

  const upsertConversation = useCallback((conv: WhatsAppConversation) => {
    setConversations((prev) => {
      const exists = prev.some((c) => c.id === conv.id);
      return exists ? prev.map((c) => (c.id === conv.id ? conv : c)) : [conv, ...prev];
    });
  }, []);

  const applyDetail = useCallback(
    (conv: WhatsAppConversation) => {
      upsertConversation(conv);
      if (selectedIdRef.current === conv.id) {
        setDetail(conv);
      }
    },
    [upsertConversation]
  );

  const refreshList = useCallback(async () => {
    const page = await serviceRef.current.listConversations('ALL', 0, 100);
    setConversations(page.items);
    return page.items;
  }, []);

  const refreshDetail = useCallback(async (id: string) => {
    const conv = await serviceRef.current.getConversation(id);
    if (selectedIdRef.current === id) {
      setDetail(conv);
    }
  }, []);

  const refreshDirectory = useCallback(async () => {
    const [contacts, groups] = await Promise.all([
      serviceRef.current.listContacts(),
      serviceRef.current.listCompanies(),
    ]);
    setDirectory(contacts);
    setCompanies(groups);
  }, []);

  const refreshAgents = useCallback(async () => {
    if (!canSupervise) return;
    setAgents(await serviceRef.current.listAgents());
  }, [canSupervise]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [items] = await Promise.all([
          refreshList(),
          refreshDirectory(),
          serviceRef.current.listTemplates().then((t) => !cancelled && setTemplates(t)),
          refreshAgents(),
        ]);
        if (!cancelled && selectedIdRef.current == null) {
          const firstAvailable = items.find(
            (c) => !c.assignedAgentId && c.status === WhatsAppConversationStatus.NEW
          );
          const firstMine = items.find(
            (c) => currentUserId != null && c.assignedAgentId === currentUserId && !isClosedConversation(c)
          );
          setSelectedId(firstAvailable?.id ?? firstMine?.id ?? null);
        }
      } catch (e) {
        if (!cancelled) notify(errorMessage(e, 'No se pudo cargar la bandeja de WhatsApp.'));
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshList, refreshDirectory, refreshAgents, currentUserId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetail((prev) => (prev?.id === selectedId ? prev : null));
    refreshDetail(selectedId).catch((e) => notify(errorMessage(e, 'No se pudo cargar la conversación.')));
  }, [selectedId, refreshDetail]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      refreshList().catch(() => undefined);
      const id = selectedIdRef.current;
      if (id) refreshDetail(id).catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refreshList, refreshDetail]);

  /** Ejecuta una acción contra el backend mostrando el error en el snackbar. */
  const run = async <T,>(action: () => Promise<T>, fallbackError: string): Promise<T | undefined> => {
    setBusy(true);
    try {
      return await action();
    } catch (e) {
      notify(errorMessage(e, fallbackError));
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  const loadAllCompanies = async () => {
    if (allCompanies.length > 0) return allCompanies;
    try {
      const accounts: Array<{ id: number; orgPath?: string }> = await accountService.getAvailableAccounts();
      const mapped = (Array.isArray(accounts) ? accounts : [])
        .map((a) => ({ id: String(a.id), name: a.orgPath ?? String(a.id) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'es'));
      setAllCompanies(mapped);
      return mapped;
    } catch (e) {
      notify(errorMessage(e, 'No se pudieron cargar las empresas.'));
      return [];
    }
  };

  const focusAssignmentTab = (conversationId: string) => {
    const conv = conversations.find((c) => c.id === conversationId);
    setSelectedId(conversationId);
    setAssignAgentId(conv?.assignedAgentId ?? '');
    setSupervisorDetailTab('assignment');
  };

  const selected = useMemo(() => {
    if (detail && detail.id === selectedId) return detail;
    return conversations.find((c) => c.id === selectedId) ?? null;
  }, [conversations, detail, selectedId]);

  useEffect(() => {
    setNotesDraft(selected?.notes ?? '');
  }, [selected?.id, selected?.notes]);

  const queueCounts = useMemo(() => {
    const count = (queue: AgentQueue) =>
      conversations.filter((c) => matchesAgentQueue(c, queue, currentUserId)).length;
    return {
      available: count('available'),
      mine: count('mine'),
      mineClosed: count('mineClosed'),
      others: count('others'),
    };
  }, [conversations, currentUserId]);

  const supervisorMetrics = useMemo(() => {
    const sinAsignar = conversations.filter((c) => c.status === WhatsAppConversationStatus.NEW).length;
    const enAtencion = conversations.filter((c) => c.status === WhatsAppConversationStatus.IN_PROGRESS).length;
    const enEspera = conversations.filter((c) => c.status === WhatsAppConversationStatus.WAITING).length;
    const cerradasHoy = conversations.filter((c) => c.status === WhatsAppConversationStatus.CLOSED).length;
    return { sinAsignar, enAtencion, enEspera, cerradasHoy };
  }, [conversations]);

  const filteredInbox = useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations.filter((c) => {
      if (!matchesAgentQueue(c, agentQueue, currentUserId)) return false;
      if (!q) return true;
      const haystack = `${c.contact.displayName ?? ''} ${c.contact.phone} ${c.lastMessagePreview} ${c.assignedAgentName ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [conversations, agentQueue, currentUserId, search]);

  const filteredSupervisorRows = useMemo(() => {
    const q = supSearch.trim().toLowerCase();
    return conversations.filter((c) => {
      if (supStatusFilter !== 'ALL' && c.status !== supStatusFilter) return false;
      if (supAgentFilter !== 'ALL') {
        if (supAgentFilter === 'UNASSIGNED' && c.assignedAgentId) return false;
        if (supAgentFilter !== 'UNASSIGNED' && c.assignedAgentId !== supAgentFilter) return false;
      }
      if (!q) return true;
      const haystack = `${c.contact.displayName ?? ''} ${c.contact.phone} ${c.lastMessagePreview} ${c.assignedAgentName ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [conversations, supStatusFilter, supAgentFilter, supSearch]);

  const onlineAgents = agents.filter((a) => a.online).length;

  const contactGroups = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    const match = (c: WhatsAppDirectoryContact) => {
      if (!q) return true;
      return `${c.displayName ?? ''} ${c.phone}`.toLowerCase().includes(q);
    };
    const groups: { id: string; name: string; contacts: WhatsAppDirectoryContact[] }[] = [
      ...companies.map((co) => ({
        id: co.id,
        name: co.name,
        contacts: directory.filter((c) => c.active && c.companyId === co.id && match(c)),
      })),
      {
        id: WHATSAPP_NO_COMPANY_ID,
        name: WHATSAPP_NO_COMPANY_LABEL,
        contacts: directory.filter((c) => c.active && c.companyId == null && match(c)),
      },
    ];
    if (contactGroupFilter === 'ALL') return groups;
    return groups.filter((g) => g.id === contactGroupFilter);
  }, [companies, directory, contactSearch, contactGroupFilter]);

  const splitName = (full?: string) => {
    const parts = (full || '').trim().split(/\s+/).filter(Boolean);
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
    };
  };

  const buildIntakePrefill = (conv: WhatsAppConversation, companyId: string, companyName?: string | null) => {
    const { firstName, lastName } = splitName(conv.contact.displayName);
    return {
      accountId: companyId,
      accountName: companyName || undefined,
      lockAccount: true,
      mobilePhone: conv.contact.phone.replace(/\D/g, '').slice(-10) || conv.contact.phone,
      firstName,
      lastName,
      email: conv.contact.email,
    };
  };

  const openIntakePanel = (conv: WhatsAppConversation, prefill: WhatsAppIntakePrefill | null, phoneCallId: number | null) => {
    setResumePhoneCallId(phoneCallId);
    setIntakePrefill(prefill);
    setIntakeConversationId(conv.id);
    setCaptureWizardOpen(true);
  };

  const openCreateIntake = () => {
    if (!selected || !canOpenIntake) return;
    const existingId = intakeIdFor(selected);
    const companyId = selected.contact.companyId;
    const prefill = companyId
      ? buildIntakePrefill(selected, companyId, selected.contact.companyName)
      : null;
    if (existingId) {
      openIntakePanel(selected, prefill, existingId);
      return;
    }
    if (companyId) {
      openIntakePanel(selected, prefill, null);
      return;
    }
    setPickCompanyId('');
    setPickCompanyOpen(true);
    void loadAllCompanies();
  };

  const confirmPickCompanyForIntake = async () => {
    if (!selected || !pickCompanyId || !selected.contact.contactId) return;
    const company = allCompanies.find((c) => c.id === pickCompanyId);
    if (!company) return;
    const conv = selected;
    // Regla A: contacto Sin empresa pasa a la empresa elegida para el Intake
    const updated = await run(
      () => whatsappService.assignContactAccount(conv.contact.contactId as string, company.id),
      'No se pudo asignar la empresa al contacto.'
    );
    if (!updated) return;
    setDirectory((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    refreshDetail(conv.id).catch(() => undefined);
    refreshDirectory().catch(() => undefined);
    setPickCompanyOpen(false);
    openIntakePanel(conv, buildIntakePrefill(conv, company.id, company.name), intakeIdFor(conv));
  };

  const handleAssociateIntake = async (intake: WhatsAppIntakeOption) => {
    if (!selected) return;
    const conv = await run(
      () => whatsappService.associateIntake(selected.id, intake.id),
      'No se pudo asociar el Intake.'
    );
    if (!conv) return;
    forgetIntakeDraft(selected.id);
    applyDetail(conv);
    refreshDirectory().catch(() => undefined);
    setAssociateIntakeOpen(false);
    notify('Intake asociado a la conversación.', 'success');
  };

  const startOutboundConversation = async (contact: WhatsAppDirectoryContact, template: WhatsAppApprovedTemplate) => {
    const conv = await run(
      () => whatsappService.startOutbound(contact.id, template.id),
      'No se pudo iniciar la conversación.'
    );
    if (!conv) return;
    selectedIdRef.current = conv.id;
    setSelectedId(conv.id);
    applyDetail(conv);
    setAgentLeftTab('chats');
    setAgentQueue(isClosedConversation(conv) ? 'mineClosed' : 'mine');
    setOutboundOpen(false);
  };

  const handleDraftReady = useCallback(
    (phoneCallId: number) => {
      const conversationId = intakeConversationIdRef.current;
      if (!conversationId) return;
      rememberIntakeDraft(conversationId, phoneCallId);
      setResumePhoneCallId(phoneCallId);
    },
    [rememberIntakeDraft]
  );

  const handleCaptureWizardClose = async (_refresh = false, phoneCallId?: number | null) => {
    setCaptureWizardOpen(false);
    const conversationId = intakeConversationId;
    setIntakePrefill(null);
    setIntakeConversationId(null);
    if (!conversationId || phoneCallId == null) return;
    rememberIntakeDraft(conversationId, phoneCallId);
    const conv =
      conversations.find((c) => c.id === conversationId) ??
      (detail?.id === conversationId ? detail : null);
    if (parseIntakeId(conv?.intakeId)) {
      forgetIntakeDraft(conversationId);
      return;
    }
    try {
      const updated = await whatsappService.associateIntake(conversationId, phoneCallId, true);
      forgetIntakeDraft(conversationId);
      applyDetail(updated);
      notify('Intake creado y asociado a la conversación.', 'success');
    } catch (e) {
      const message = errorMessage(e, '');
      if (message.toLowerCase().includes('no tiene empresa')) return;
      notify(
        (message || 'El Intake se guardó, pero no se pudo asociar a la conversación.') +
          ' Puedes seguirlo con Continuar intake.',
        'warning'
      );
    }
  };

  const takeConversation = async (conversationId: string) => {
    const conv = await run(
      () => whatsappService.takeConversation(conversationId),
      'No se pudo tomar la conversación.'
    );
    if (!conv) return;
    selectedIdRef.current = conversationId;
    setSelectedId(conversationId);
    applyDetail(conv);
    setAgentQueue('mine');
    setToastOpen(false);
  };

  const assignOrReassign = async (conversationId: string, agentId: string) => {
    const conv = await run(
      () => whatsappService.assignConversation(conversationId, agentId),
      'No se pudo asignar la conversación.'
    );
    if (!conv) return;
    applyDetail(conv);
    refreshAgents().catch(() => undefined);
    notify('Conversación asignada.', 'success');
  };

  const sendMessage = async () => {
    if (!selected || !draft.trim() || busy) return;
    if (!selected.assignedAgentId) return;
    if (isAwaitingContactReply(selected)) return;
    const body = draft.trim();
    const conv = await run(() => whatsappService.sendMessage(selected.id, body), 'No se pudo enviar el mensaje.');
    if (!conv) return;
    applyDetail(conv);
    setDraft('');
  };

  const saveNotes = async () => {
    if (!selected) return;
    const conv = await run(
      () => whatsappService.updateNotes(selected.id, notesDraft),
      'No se pudieron guardar las notas.'
    );
    if (!conv) return;
    applyDetail(conv);
    notify('Notas guardadas.', 'success');
  };

  const openEditContact = () => {
    if (!selected) return;
    setEditContactForm({
      displayName: selected.contact.displayName ?? '',
      email: selected.contact.email ?? '',
      companyId: selected.contact.companyId ?? '',
    });
    setEditContactOpen(true);
    if (!selected.contact.companyId) {
      loadAllCompanies();
    }
  };

  const editContactEmailInvalid =
    editContactForm.email.trim() !== '' && !EMAIL_REGEX.test(editContactForm.email.trim());

  const confirmEditContact = async () => {
    const contactId = selected?.contact.contactId;
    if (!selected || !contactId || editContactEmailInvalid) return;
    const assignCompany = !selected.contact.companyId && editContactForm.companyId !== '';
    const updated = await run(
      () =>
        whatsappService.updateContact(contactId, {
          displayName: editContactForm.displayName.trim(),
          email: editContactForm.email.trim(),
          accountId: assignCompany ? Number(editContactForm.companyId) : null,
        }),
      'No se pudieron guardar los datos del contacto.'
    );
    if (!updated) return;
    setDirectory((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    refreshList().catch(() => undefined);
    refreshDetail(selected.id).catch(() => undefined);
    if (assignCompany) refreshDirectory().catch(() => undefined);
    setEditContactOpen(false);
    notify('Datos del contacto actualizados.', 'success');
  };

  const pendingUnassigned = conversations.filter((c) => c.status === WhatsAppConversationStatus.NEW);
  const canCloseSelected =
    selected != null &&
    selected.status !== WhatsAppConversationStatus.CLOSED &&
    (hasAnyRole([UserRole.ADMIN, UserRole.SUPERVISOR]) ||
      (currentUserId != null && selected.assignedAgentId === currentUserId));

  const confirmCloseConversation = async () => {
    if (!selected || !canCloseSelected) return;
    const conv = await run(
      () => whatsappService.closeConversation(selected.id, closeReason.trim() || undefined),
      'No se pudo cerrar la conversación.'
    );
    if (!conv) return;
    applyDetail(conv);
    setCloseDialogOpen(false);
    setCloseReason('');
    refreshAgents().catch(() => undefined);
    notify('Conversación cerrada.', 'success');
  };

  return (
    <Box
      sx={{
        height: 'calc(100vh - 112px)',
        maxHeight: 'calc(100vh - 112px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon color="success" />
            {viewMode === 'agent' ? 'Atención WhatsApp' : 'Supervisión de conversaciones'}
          </Typography>
          {viewMode === 'supervisor' && (
            <Typography variant="body2" color="text.secondary">
              Consulta, asigna y reasigna conversaciones del canal WhatsApp
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {initialLoading && <CircularProgress size={20} />}
          {canSupervise && (
            <Chip
              size="small"
              label={`${onlineAgents} agentes en línea`}
              sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
            />
          )}
          {canSupervise && (
            <Tabs
              value={viewMode}
              onChange={(_, v: 'agent' | 'supervisor') => setViewMode(v)}
              textColor="primary"
              indicatorColor="primary"
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, textTransform: 'none' } }}
            >
              <Tab value="agent" label="Bandeja agente" />
              <Tab value="supervisor" label="Supervisión" />
            </Tabs>
          )}
        </Stack>
      </Box>

      {viewMode === 'agent' ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: captureWizardOpen
              ? { xs: '1fr', lg: 'minmax(320px, 0.9fr) minmax(480px, 1.15fr)' }
              : { xs: '1fr', md: '300px 1fr 300px' },
            gridTemplateRows: captureWizardOpen
              ? { xs: 'minmax(240px, 42%) minmax(0, 1fr)', lg: 'minmax(0, 1fr)' }
              : 'minmax(0, 1fr)',
          }}
        >
          {/* Lista */}
          <Paper
            sx={{
              display: captureWizardOpen ? 'none' : 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Bandeja
              </Typography>
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 1.5, textTransform: 'none' }}
                onClick={() => setOutboundOpen(true)}
              >
                + Nueva conversación
              </Button>
              <Tabs
                value={agentLeftTab}
                onChange={(_, v: 'chats' | 'contacts') => setAgentLeftTab(v)}
                variant="fullWidth"
                sx={{ mt: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, textTransform: 'none', fontSize: '0.8rem' } }}
              >
                <Tab value="chats" label="Conversaciones" />
                <Tab value="contacts" label="Contactos" />
              </Tabs>
              {agentLeftTab === 'chats' ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Tabs
                      value={agentQueue === 'available' || agentQueue === 'mine' ? agentQueue : false}
                      onChange={(_, v: AgentQueue) => setAgentQueue(v)}
                      variant="fullWidth"
                      sx={{ flex: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, px: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
                    >
                      <Tab value="available" label={`Por tomar (${queueCounts.available})`} />
                      <Tab value="mine" label={`Mías (${queueCounts.mine})`} />
                    </Tabs>
                    <Button
                      size="small"
                      color={agentQueue === 'mineClosed' || agentQueue === 'others' ? 'primary' : 'inherit'}
                      onClick={(e) => setMoreAnchor(e.currentTarget)}
                      sx={{ textTransform: 'none', minWidth: 48, fontWeight: 600 }}
                    >
                      Más
                    </Button>
                    <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                      <MenuItem
                        selected={agentQueue === 'mineClosed'}
                        onClick={() => {
                          setAgentQueue('mineClosed');
                          setMoreAnchor(null);
                        }}
                      >
                        Cerradas mías ({queueCounts.mineClosed})
                      </MenuItem>
                      <MenuItem
                        selected={agentQueue === 'others'}
                        onClick={() => {
                          setAgentQueue('others');
                          setMoreAnchor(null);
                        }}
                      >
                        De otros agentes ({queueCounts.others})
                      </MenuItem>
                    </Menu>
                  </Box>
                  {(agentQueue === 'mineClosed' || agentQueue === 'others') && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {agentQueue === 'mineClosed' ? 'Viendo tus conversaciones cerradas' : 'Viendo conversaciones de otros agentes'}
                    </Typography>
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar conversaciones..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mt: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Empresa</InputLabel>
                    <Select
                      label="Empresa"
                      value={contactGroupFilter}
                      onChange={(e) => setContactGroupFilter(e.target.value)}
                    >
                      <MenuItem value="ALL">Todos</MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                      <MenuItem value={WHATSAPP_NO_COMPANY_ID}>{WHATSAPP_NO_COMPANY_LABEL}</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar contactos..."
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              )}
            </Box>
            {agentLeftTab === 'chats' ? (
            <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {filteredInbox.map((c) => (
                <ListItemButton
                  key={c.id}
                  selected={c.id === selectedId}
                  onClick={() => setSelectedId(c.id)}
                  alignItems="flex-start"
                  sx={{ borderBottom: 1, borderColor: 'divider', py: 1.25 }}
                >
                  <Avatar sx={{ width: 40, height: 40, mr: 1.5, bgcolor: BRAND_COLORS.mediumPurple, fontSize: 14 }}>
                    {(c.contact.displayName ?? c.contact.phone).slice(0, 2).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {c.contact.displayName?.trim() || c.contact.phone}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatClock(c.lastMessageAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {c.lastMessagePreview}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mt: 0.5 }}>
                          {queueRowHint(c, agentQueue) && (
                            <Chip
                              size="small"
                              label={queueRowHint(c, agentQueue)}
                              color={c.awaitingContactReply && agentQueue === 'mine' ? 'info' : agentQueue === 'mine' ? 'warning' : 'default'}
                              variant={agentQueue === 'mine' ? 'filled' : 'outlined'}
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                          {agentQueue === 'available' && (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busy}
                              onClick={(e) => {
                                e.stopPropagation();
                                void takeConversation(c.id);
                              }}
                              sx={{ textTransform: 'none', minWidth: 0, py: 0, px: 1 }}
                            >
                              Tomar
                            </Button>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
              {filteredInbox.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {queueEmptyMessage[agentQueue]}
                  </Typography>
                </Box>
              )}
            </List>
            ) : (
            <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {contactGroups.map((group) => (
                <Box key={group.id}>
                  <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {group.name} ({group.contacts.length})
                    </Typography>
                  </Box>
                  {group.contacts.map((ct) => (
                    <ListItemButton
                      key={ct.id}
                      onClick={() => {
                        const openConv = conversations.find(
                          (c) =>
                            c.contact.contactId === ct.id &&
                            c.status !== WhatsAppConversationStatus.CLOSED
                        );
                        if (openConv) {
                          setSelectedId(openConv.id);
                          setAgentLeftTab('chats');
                        } else {
                          setOutboundOpen(true);
                        }
                      }}
                      sx={{ borderBottom: 1, borderColor: 'divider', py: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {ct.displayName?.trim() || ct.phone}
                          </Typography>
                        }
                        secondary={ct.displayName ? ct.phone : 'Sin nombre'}
                      />
                    </ListItemButton>
                  ))}
                  {group.contacts.length === 0 && (
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Sin contactos
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </List>
            )}
          </Paper>

          {/* Chat */}
          <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative', overflow: 'hidden' }}>
            {selected ? (
              <>
                <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {contactTitle(selected)}
                      </Typography>
                      {selected.contact.identified && (
                        <Chip size="small" icon={<IdentifiedIcon />} label="Usuario identificado" color="success" variant="outlined" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {selected.contact.phone}
                      {selected.assignedAgentName ? ` · Agente: ${selected.assignedAgentName}` : ' · Sin asignar'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selected.windowHoursRemaining != null && selected.status !== WhatsAppConversationStatus.CLOSED && (
                      <Tooltip title="Ventana de 24 h de WhatsApp para mensajes libres desde el último mensaje del contacto">
                        <Chip
                          size="small"
                          icon={<TimeIcon />}
                          label={
                            selected.windowHoursRemaining > 0
                              ? `${selected.windowHoursRemaining} h restantes`
                              : 'Ventana cerrada'
                          }
                          color={selected.windowHoursRemaining > 0 ? 'default' : 'warning'}
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {canCloseSelected && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        onClick={() => {
                          setCloseReason('');
                          setCloseDialogOpen(true);
                        }}
                      >
                        Cerrar
                      </Button>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, position: 'relative', ...CHAT_WALLPAPER_SX }}>
                  {!selected.detailLoaded && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}
                  <ChatMessageBubbles messages={selected.messages} />

                  {!selected.assignedAgentId && selected.status !== WhatsAppConversationStatus.CLOSED && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(17, 27, 33, 0.28)',
                        p: 2,
                      }}
                    >
                      <Paper sx={{ p: 3, textAlign: 'center', maxWidth: 360 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                          Conversación sin asignar
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Tómala para atender al usuario y evitar respuestas simultáneas.
                        </Typography>
                        <Button
                          variant="contained"
                          size="large"
                          disabled={busy}
                          onClick={() => takeConversation(selected.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Tomar conversación
                        </Button>
                      </Paper>
                    </Box>
                  )}
                </Box>

                {(() => {
                  const awaitingReply = isAwaitingContactReply(selected);
                  const isClosed = selected.status === WhatsAppConversationStatus.CLOSED;
                  const assignedToOther =
                    Boolean(selected.assignedAgentId) && selected.assignedAgentId !== currentUserId;
                  const windowClosed = !awaitingReply && selected.windowHoursRemaining === 0;
                  const composerLocked =
                    !selected.assignedAgentId ||
                    isClosed ||
                    awaitingReply ||
                    assignedToOther ||
                    windowClosed ||
                    busy;
                  return (
                    <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                      {awaitingReply && !isClosed && (
                        <Alert severity="info" sx={{ borderRadius: 0 }}>
                          Mensaje enviado. Cuando el contacto responda, podrás continuar la conversación.
                        </Alert>
                      )}
                      {!awaitingReply && !isClosed && assignedToOther && (
                        <Alert severity="info" sx={{ borderRadius: 0 }}>
                          Conversación asignada a {selected.assignedAgentName ?? 'otro agente'}. Solo el agente asignado
                          puede responder.
                        </Alert>
                      )}
                      {windowClosed && !isClosed && !assignedToOther && selected.assignedAgentId && (
                        <Alert severity="warning" sx={{ borderRadius: 0 }}>
                          Pasaron más de 24 h desde el último mensaje del contacto. WhatsApp solo permite retomar con un
                          mensaje inicial aprobado (Nueva conversación).
                        </Alert>
                      )}
                      <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton size="small" disabled>
                          <AttachIcon />
                        </IconButton>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={
                            isClosed
                              ? 'Conversación cerrada'
                              : awaitingReply
                                ? 'Esperando respuesta del contacto...'
                                : !selected.assignedAgentId
                                  ? 'Toma la conversación para responder'
                                  : assignedToOther
                                    ? 'Asignada a otro agente'
                                    : windowClosed
                                      ? 'Ventana de 24 h cerrada'
                                      : 'Escribe un mensaje...'
                          }
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          disabled={composerLocked}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          endIcon={<SendIcon />}
                          onClick={sendMessage}
                          disabled={composerLocked || !draft.trim()}
                          sx={{ textTransform: 'none' }}
                        >
                          Enviar
                        </Button>
                      </Box>
                    </Box>
                  );
                })()}
              </>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">Selecciona una conversación</Typography>
              </Box>
            )}
          </Paper>

          {/* Panel lateral o intake junto al chat */}
          {captureWizardOpen ? (
            <Box sx={{ minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <CaptureWizardModal
                open
                embedded
                onClose={handleCaptureWizardClose}
                onDraftReady={handleDraftReady}
                initialPhoneCallId={resumePhoneCallId}
                whatsappPrefill={intakePrefill}
              />
            </Box>
          ) : (
          <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto', p: 1.5, gap: 1.5 }}>
            {toastOpen && pendingUnassigned.length > 0 && (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => takeConversation(pendingUnassigned[0].id)}
                    sx={{ textTransform: 'none' }}
                  >
                    Tomar
                  </Button>
                }
                onClose={() => setToastOpen(false)}
              >
                Nueva conversación pendiente
              </Alert>
            )}

            {selected ? (
              <>
                <Box>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar sx={{ bgcolor: BRAND_COLORS.darkPurple }}>
                      {contactTitle(selected).slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {contactTitle(selected)}
                      </Typography>
                      <Chip
                        size="small"
                        label={selected.contact.identified ? 'Usuario identificado' : 'Sin identificar'}
                        color={selected.contact.identified ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {selected.contact.phone}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Empresa:{' '}
                    <strong>
                      {selected.intakeCompanyName ||
                        selected.contact.companyName ||
                        WHATSAPP_NO_COMPANY_LABEL}
                    </strong>
                    {selected.intakeCompanyName ? ' (desde Intake)' : ''}
                  </Typography>
                  {selected.contact.email && (
                    <Typography variant="body2" color="text.secondary">
                      {selected.contact.email}
                    </Typography>
                  )}
                  {selected.contact.registeredAt && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Alta: {selected.contact.registeredAt}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    startIcon={<PersonSearchIcon />}
                    sx={{ mt: 1, textTransform: 'none' }}
                    disabled={
                      busy || !selected.contact.contactId || selected.status === WhatsAppConversationStatus.CLOSED
                    }
                    onClick={openEditContact}
                  >
                    Editar datos
                  </Button>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Intake
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {selected.intakeLabel ??
                      (intakeIdFor(selected) ? `Borrador #${intakeIdFor(selected)}` : 'Sin Intake asociado')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      disabled={Boolean(selected.intakeId) || pendingIntakeDrafts[selected.id] != null}
                      onClick={() => setAssociateIntakeOpen(true)}
                    >
                      Asociar Intake
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ textTransform: 'none' }}
                      disabled={!canOpenIntake}
                      onClick={openCreateIntake}
                    >
                      {intakeIdFor(selected) ? 'Continuar intake' : 'Crear Intake'}
                    </Button>
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Notas internas
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Notas visibles solo para el equipo"
                  />
                  <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={saveNotes}>
                    Guardar
                  </Button>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Trazabilidad
                  </Typography>
                  <Stack spacing={1}>
                    {[...selected.trace].reverse().map((t) => (
                      <Box key={t.id} sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 1.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatClock(t.at)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t.label}
                        </Typography>
                        {t.detail && (
                          <Typography variant="caption" color="text.secondary">
                            {t.detail}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Selecciona una conversación para ver el detalle.
              </Typography>
            )}
          </Paper>
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, gap: 1.5, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, gap: 1.5, minWidth: 0, overflow: 'hidden' }}>
            <Grid container spacing={1.5} sx={{ flexShrink: 0 }}>
              {(
                [
                  { label: 'Sin asignar', value: supervisorMetrics.sinAsignar, color: BRAND_COLORS.darkPurple },
                  { label: 'En atención', value: supervisorMetrics.enAtencion, color: '#2e7d32' },
                  { label: 'En espera', value: supervisorMetrics.enEspera, color: '#ed6c02' },
                  { label: 'Cerradas hoy', value: supervisorMetrics.cerradasHoy, color: '#616161' },
                ] as const
              ).map((m) => (
                <Grid item xs={6} md={3} key={m.label}>
                  <Paper sx={{ p: 1.5, borderLeft: 4, borderColor: m.color }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: m.color }}>
                      {m.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {m.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
              <TextField
                size="small"
                placeholder="Buscar..."
                value={supSearch}
                onChange={(e) => setSupSearch(e.target.value)}
                sx={{ minWidth: 200, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Estado</InputLabel>
                <Select label="Estado" value={supStatusFilter} onChange={(e) => setSupStatusFilter(e.target.value)}>
                  <MenuItem value="ALL">Todos</MenuItem>
                  <MenuItem value={WhatsAppConversationStatus.NEW}>Sin asignar</MenuItem>
                  <MenuItem value={WhatsAppConversationStatus.IN_PROGRESS}>En atención</MenuItem>
                  <MenuItem value={WhatsAppConversationStatus.WAITING}>En espera</MenuItem>
                  <MenuItem value={WhatsAppConversationStatus.CLOSED}>Cerrada</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Agente</InputLabel>
                <Select label="Agente" value={supAgentFilter} onChange={(e) => setSupAgentFilter(e.target.value)}>
                  <MenuItem value="ALL">Todos</MenuItem>
                  <MenuItem value="UNASSIGNED">Sin asignar</MenuItem>
                  {agents.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>

            <TableContainer
              component={Paper}
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                height: 0,
                overflow: 'auto',
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Último mensaje</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Agente</TableCell>
                    <TableCell>Espera</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSupervisorRows.map((c) => (
                    <TableRow
                      key={c.id}
                      hover
                      selected={c.id === selectedId}
                      onClick={() => {
                        setSelectedId(c.id);
                        setAssignAgentId(c.assignedAgentId ?? '');
                        setSupervisorDetailTab('conversation');
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WhatsAppIcon fontSize="small" color="success" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.contact.phone}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                          {c.lastMessagePreview}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatClock(c.lastMessageAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={statusLabel[c.status]} color={statusColor[c.status]} />
                      </TableCell>
                      <TableCell>{c.assignedAgentName ?? '—'}</TableCell>
                      <TableCell>{c.waitingMinutes != null && c.waitingMinutes > 0 ? `${c.waitingMinutes} min` : '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AssignIcon />}
                          sx={{ textTransform: 'none', mr: 0.5 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            focusAssignmentTab(c.id);
                          }}
                        >
                          {c.assignedAgentId ? 'Reasignar' : 'Asignar'}
                        </Button>
                        <IconButton size="small">
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%', overflow: 'hidden', width: '100%' }}>
            {selected ? (
              <>
                <Box sx={{ px: 1.5, pt: 1.25, flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                        {contactTitle(selected)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {selected.contact.phone}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" label={statusLabel[selected.status]} color={statusColor[selected.status]} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {selected.channel ?? 'WhatsApp'} · {selected.origin ?? '—'}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setSelectedId(null)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Tabs
                    value={supervisorDetailTab}
                    onChange={(_, v: 'conversation' | 'assignment') => {
                      setSupervisorDetailTab(v);
                      if (v === 'assignment') {
                        setAssignAgentId(selected.assignedAgentId ?? '');
                      }
                    }}
                    variant="fullWidth"
                    sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, px: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
                  >
                    <Tab value="conversation" label="Conversación" />
                    <Tab value="assignment" label="Asignación" />
                  </Tabs>
                </Box>
                <Divider />

                {supervisorDetailTab === 'conversation' ? (
                  <Box
                    sx={{
                      flex: '1 1 auto',
                      minHeight: 0,
                      height: 0,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      p: 1.25,
                      ...CHAT_WALLPAPER_SX,
                    }}
                  >
                    <ChatMessageBubbles messages={selected.messages} />
                  </Box>
                ) : (
                  <Box sx={{ flex: '1 1 auto', minHeight: 0, height: 0, overflowY: 'auto', p: 1.5 }}>
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        {selected.assignedAgentName
                          ? `Agente actual: ${selected.assignedAgentName}`
                          : 'Sin agente asignado'}
                      </Typography>
                      <FormControl fullWidth size="small">
                        <InputLabel>Agente</InputLabel>
                        <Select
                          label="Agente"
                          value={assignAgentId}
                          onChange={(e) => setAssignAgentId(e.target.value)}
                        >
                          {agents.map((a) => (
                            <MenuItem key={a.id} value={a.id}>
                              {a.fullName}
                              {a.online ? '' : ' (offline)'}
                              {a.paused ? ' · pausado' : ''}
                              {a.openConversations ? ` · ${a.openConversations} abiertas` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                        disabled={!assignAgentId}
                        onClick={() => {
                          assignOrReassign(selected.id, assignAgentId);
                          setSupervisorDetailTab('conversation');
                        }}
                      >
                        {selected.assignedAgentId ? 'Reasignar conversación' : 'Asignar conversación'}
                      </Button>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Agentes disponibles
                        </Typography>
                        <Stack spacing={0.75}>
                          {agents.map((a) => (
                            <Stack key={a.id} direction="row" spacing={1} alignItems="center">
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: !a.online ? 'grey.400' : a.paused ? 'warning.main' : 'success.main',
                                }}
                              />
                              <Typography variant="body2">{a.fullName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {!a.online ? 'offline' : a.paused ? 'pausado' : 'en línea'}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Typography color="text.secondary" align="center">
                  Selecciona una conversación para ver el historial completo
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <NewOutboundConversationDialog
        open={outboundOpen}
        companies={companies}
        contacts={directory}
        templates={templates}
        submitting={busy}
        onClose={() => setOutboundOpen(false)}
        onStart={startOutboundConversation}
      />

      <AssociateIntakeDialog
        open={associateIntakeOpen}
        searchIntakes={whatsappService.searchIntakes}
        contact={selected?.contact ?? null}
        submitting={busy}
        onClose={() => setAssociateIntakeOpen(false)}
        onAssociate={handleAssociateIntake}
      />

      <Dialog open={pickCompanyOpen} onClose={() => setPickCompanyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Empresa del Intake</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            El contacto está en <strong>{WHATSAPP_NO_COMPANY_LABEL}</strong>. Al crear el Intake se
            moverá a la empresa que elijas.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Empresa</InputLabel>
            <Select
              label="Empresa"
              value={pickCompanyId}
              onChange={(e) => setPickCompanyId(e.target.value)}
            >
              {allCompanies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickCompanyOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!pickCompanyId || busy}
            onClick={confirmPickCompanyForIntake}
            sx={{ textTransform: 'none' }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cerrar conversación</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La conversación pasará a <strong>Cerradas</strong>. Si el contacto vuelve a escribir se abrirá una
            conversación nueva.
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Motivo (opcional)"
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={confirmCloseConversation}
            sx={{ textTransform: 'none' }}
          >
            Cerrar conversación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editContactOpen} onClose={() => setEditContactOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar datos del contacto</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              size="small"
              fullWidth
              label="Teléfono"
              value={selected?.contact.phone ?? ''}
              disabled
              helperText="El teléfono identifica al contacto en WhatsApp y no se puede modificar."
            />
            <TextField
              size="small"
              fullWidth
              label="Nombre"
              value={editContactForm.displayName}
              onChange={(e) => setEditContactForm((f) => ({ ...f, displayName: e.target.value }))}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              size="small"
              fullWidth
              label="Correo"
              type="email"
              value={editContactForm.email}
              onChange={(e) => setEditContactForm((f) => ({ ...f, email: e.target.value }))}
              error={editContactEmailInvalid}
              helperText={editContactEmailInvalid ? 'Formato de correo inválido.' : ' '}
              inputProps={{ maxLength: 200 }}
            />
            {selected?.contact.companyId ? (
              <TextField
                size="small"
                fullWidth
                label="Empresa"
                value={selected.contact.companyName ?? ''}
                disabled
                helperText="El contacto ya tiene empresa asignada y no se puede cambiar."
              />
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Empresa</InputLabel>
                <Select
                  label="Empresa"
                  value={editContactForm.companyId}
                  onChange={(e) => setEditContactForm((f) => ({ ...f, companyId: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>{WHATSAPP_NO_COMPANY_LABEL}</em>
                  </MenuItem>
                  {allCompanies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!selected?.contact.companyId && editContactForm.companyId !== '' && (
              <Alert severity="warning">
                Una vez asignada, la empresa del contacto no se podrá cambiar.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditContactOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={busy || editContactEmailInvalid}
            onClick={confirmEditContact}
            sx={{ textTransform: 'none' }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar != null}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar?.severity ?? 'info'}
          onClose={() => setSnackbar(null)}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppInboxPage;
